import { Recipe } from "@/types/recipe";

export interface SimilarityMatch {
  recipe: Recipe;
  score: number;
  reasons: string[];
}

export interface RecipeSimilarityService {
  findSimilarRecipes: (suggestion: string, familyRecipes: Recipe[], limit?: number) => SimilarityMatch[];
}

/**
 * Extract key components from a recipe name for similarity comparison
 */
function extractRecipeComponents(recipeName: string) {
  const name = recipeName.toLowerCase();
  
  // Helper function to check for whole word matches using word boundaries
  const matchesWholeWord = (text: string, word: string): boolean => {
    const regex = new RegExp(`\\b${word}\\b`, 'i');
    return regex.test(text);
  };
  
  // Common proteins
  const proteins = ['chicken', 'beef', 'pork', 'lamb', 'fish', 'salmon', 'tuna', 'shrimp', 'turkey', 'duck', 'tofu', 'tempeh'];
  const foundProteins = proteins.filter(protein => matchesWholeWord(name, protein));
  
  // Common cooking methods
  const cookingMethods = ['grilled', 'roasted', 'braised', 'fried', 'baked', 'sauteed', 'steamed', 'poached', 'smoked', 'barbecued', 'stir-fried'];
  const foundMethods = cookingMethods.filter(method => 
    matchesWholeWord(name, method) || matchesWholeWord(name, method.replace('-', ' '))
  );
  
  // Common cuisines/flavors
  const cuisines = ['italian', 'mexican', 'asian', 'chinese', 'thai', 'indian', 'mediterranean', 'french', 'spanish', 'greek'];
  const foundCuisines = cuisines.filter(cuisine => matchesWholeWord(name, cuisine));
  
  // Common flavor profiles
  const flavors = ['spicy', 'sweet', 'sour', 'savory', 'smoky', 'creamy', 'tangy', 'herbs', 'garlic', 'ginger', 'lemon', 'lime'];
  const foundFlavors = flavors.filter(flavor => matchesWholeWord(name, flavor));
  
  // Dish type categorization
  const dishTypes = {
    soup: ['soup', 'bisque', 'broth', 'chowder', 'stew', 'gazpacho', 'minestrone'],
    grilled: ['grilled', 'barbecued', 'bbq', 'charred'],
    pasta: ['pasta', 'spaghetti', 'linguine', 'penne', 'fettuccine', 'ravioli', 'lasagna'],
    salad: ['salad', 'slaw', 'greens'],
    sandwich: ['sandwich', 'burger', 'wrap', 'panini', 'sub'],
    rice: ['rice', 'risotto', 'pilaf', 'biryani'],
    pizza: ['pizza', 'flatbread'],
    dessert: ['cake', 'pie', 'tart', 'cookies', 'ice cream', 'pudding']
  };
  
  let foundDishType = '';
  for (const [type, keywords] of Object.entries(dishTypes)) {
    if (keywords.some(keyword => matchesWholeWord(name, keyword))) {
      foundDishType = type;
      break;
    }
  }
  
  // Extract main ingredients (excluding common words and cooking methods)
  const excludeWords = [
    'the', 'and', 'with', 'in', 'of', 'for', 'to', 'a', 'an', 'is', 'are', 'was', 'were', 'on', 'at', 'by',
    'recipe', 'easy', 'quick', 'simple', 'homemade', 'fresh', 'delicious', 'perfect', 'best',
    'grilled', 'roasted', 'braised', 'fried', 'baked', 'sauteed', 'steamed', 'poached', 'smoked'
  ];
  const words = name.split(/[\s\-&,]+/).filter(word => 
    word.length > 2 && 
    !excludeWords.includes(word) &&
    !foundMethods.includes(word)
  );
  
  return {
    proteins: foundProteins,
    cookingMethods: foundMethods,
    cuisines: foundCuisines,
    flavors: foundFlavors,
    dishType: foundDishType,
    keywords: words
  };
}

/**
 * Calculate similarity score between two recipe names
 */
function calculateSimilarity(suggestionName: string, recipeName: string, recipeTags: string[] = []): { score: number; reasons: string[] } {
  const suggestion = extractRecipeComponents(suggestionName);
  const recipe = extractRecipeComponents(recipeName);
  const tags = recipeTags.map(tag => tag.toLowerCase());
  
  let score = 0;
  const reasons: string[] = [];
  
  // Exact phrase matches (very high weight) - check this first
  const suggestionLower = suggestionName.toLowerCase();
  const recipeLower = recipeName.toLowerCase();
  if (suggestionLower.includes(recipeLower) || recipeLower.includes(suggestionLower)) {
    score += 50;
    reasons.push('Very similar recipe names');
  }
  
  // Protein match (highest weight - this should dominate scoring)
  const proteinMatches = suggestion.proteins.filter(p => recipe.proteins.includes(p));
  if (proteinMatches.length > 0) {
    score += 40;
    reasons.push(`Same protein: ${proteinMatches.join(', ')}`);
  }
  
  // Dish type compatibility check
  const dishTypeMatch = suggestion.dishType && recipe.dishType && suggestion.dishType === recipe.dishType;
  const dishTypeConflict = suggestion.dishType && recipe.dishType && suggestion.dishType !== recipe.dishType;
  
  if (dishTypeMatch) {
    score += 25;
    reasons.push(`Same dish type: ${suggestion.dishType}`);
  } else if (dishTypeConflict) {
    // Penalize incompatible dish types (e.g., soup vs grilled)
    const incompatibleTypes = [
      ['soup', 'grilled'], ['soup', 'sandwich'], ['salad', 'pasta'], 
      ['dessert', 'soup'], ['dessert', 'grilled']
    ];
    const isIncompatible = incompatibleTypes.some(([type1, type2]) => 
      (suggestion.dishType === type1 && recipe.dishType === type2) ||
      (suggestion.dishType === type2 && recipe.dishType === type1)
    );
    if (isIncompatible) {
      score -= 15;
    }
  }
  
  // Cooking method match (medium weight, but reduced if no protein match)
  const methodMatches = suggestion.cookingMethods.filter(m => recipe.cookingMethods.includes(m));
  if (methodMatches.length > 0) {
    const methodScore = proteinMatches.length > 0 ? 20 : 10; // Reduced weight without protein match
    score += methodScore;
    reasons.push(`Same cooking method: ${methodMatches.join(', ')}`);
  }
  
  // Cuisine match (medium weight)
  const cuisineMatches = suggestion.cuisines.filter(c => recipe.cuisines.includes(c));
  if (cuisineMatches.length > 0) {
    score += 15;
    reasons.push(`Same cuisine: ${cuisineMatches.join(', ')}`);
  }
  
  // Flavor profile match (low weight)
  const flavorMatches = suggestion.flavors.filter(f => recipe.flavors.includes(f));
  if (flavorMatches.length > 0) {
    score += 10;
    reasons.push(`Similar flavors: ${flavorMatches.join(', ')}`);
  }
  
  // Tag matches (with better filtering)
  const meaningfulTagMatches = suggestion.keywords.filter(keyword => 
    keyword.length > 3 && // Only consider longer, more meaningful words
    tags.some(tag => tag.includes(keyword) || keyword.includes(tag))
  );
  if (meaningfulTagMatches.length > 0) {
    score += meaningfulTagMatches.length * 5;
    reasons.push(`Recipe tags: ${meaningfulTagMatches.join(', ')}`);
  }
  
  // Keyword overlap (very low weight, with quality filtering)
  const meaningfulKeywordMatches = suggestion.keywords.filter(k => 
    recipe.keywords.includes(k) && 
    k.length > 3 && // Only meaningful ingredients
    !['tomatoes', 'onions', 'garlic', 'salt', 'pepper', 'oil', 'butter'].includes(k) // Exclude very common ingredients
  );
  if (meaningfulKeywordMatches.length > 0) {
    score += meaningfulKeywordMatches.length * 3;
    reasons.push(`Similar ingredients: ${meaningfulKeywordMatches.join(', ')}`);
  }
  
  // Remove duplicate or redundant reasons
  const uniqueReasons = [...new Set(reasons)];
  
  return { score, reasons: uniqueReasons };
}

/**
 * Find similar recipes from family collection
 */
export function findSimilarRecipes(
  suggestionName: string, 
  familyRecipes: Recipe[], 
  limit: number = 3
): SimilarityMatch[] {
  const similarities: SimilarityMatch[] = [];
  
  for (const recipe of familyRecipes) {
    const { score, reasons } = calculateSimilarity(suggestionName, recipe.title, recipe.tags);
    
    // Increased minimum threshold for better quality matches
    if (score > 25) {
      similarities.push({
        recipe,
        score,
        reasons: reasons.slice(0, 3) // Limit to top 3 most relevant reasons
      });
    }
  }
  
  // Sort by score descending and return top matches
  // Prioritize protein matches by giving them extra sorting weight
  return similarities
    .sort((a, b) => {
      const aHasProtein = a.reasons.some(r => r.includes('Same protein'));
      const bHasProtein = b.reasons.some(r => r.includes('Same protein'));
      
      if (aHasProtein && !bHasProtein) return -1;
      if (!aHasProtein && bHasProtein) return 1;
      
      return b.score - a.score;
    })
    .slice(0, limit);
}

/**
 * Find exact match for a recipe name (100% match rule)
 */
export function findExactMatch(suggestionName: string, familyRecipes: Recipe[]): Recipe | null {
  const normalizedSuggestion = suggestionName.toLowerCase().trim().replace(/[^\w\s]/g, '').replace(/\s+/g, ' ');
  
  for (const recipe of familyRecipes) {
    const normalizedRecipe = recipe.title.toLowerCase().trim().replace(/[^\w\s]/g, '').replace(/\s+/g, ' ');
    
    if (normalizedSuggestion === normalizedRecipe) {
      return recipe;
    }
  }
  
  return null;
}

export const recipeSimilarityService: RecipeSimilarityService = {
  findSimilarRecipes
};